/**
 * Supabase Auth Error Translations
 * Maps Supabase error messages to user-friendly Russian messages
 */

// Map of Supabase error codes/messages to Russian translations
const ERROR_TRANSLATIONS: Record<string, string> = {
  // Authentication errors
  'Invalid login credentials': 'Неверный email или пароль',
  'invalid_credentials': 'Неверный email или пароль',
  'Invalid credentials': 'Неверный email или пароль',
  
  // Email errors
  'Email not confirmed': 'Email не подтверждён. Проверьте почту и перейдите по ссылке',
  'email_not_confirmed': 'Email не подтверждён. Проверьте почту и перейдите по ссылке',
  'User already registered': 'Этот email уже зарегистрирован',
  'already registered': 'Этот email уже зарегистрирован',
  'already exists': 'Этот email уже зарегистрирован',
  'Email address cannot be used': 'Этот email нельзя использовать',
  'invalid_email': 'Неверный формат email',
  
  // Password errors
  'Password should be at least 6 characters': 'Пароль должен быть не менее 6 символов',
  'Password should be at least 8 characters': 'Пароль должен быть не менее 8 символов',
  'Password should contain at least one character of each': 'Пароль должен содержать буквы и цифры',
  'New password should be different from the old password': 'Новый пароль должен отличаться от текущего',
  'New password should be different from the old password.': 'Новый пароль должен отличаться от текущего',
  'same_password': 'Новый пароль должен отличаться от текущего',
  'weak_password': 'Слишком простой пароль. Используйте буквы и цифры',
  'Password must contain': 'Пароль должен содержать буквы и цифры',
  
  // Rate limiting
  'For security purposes, you can only request this once every 60 seconds': 'Подождите минуту перед повторной попыткой',
  'For security purposes, you can only request this after': 'Подождите немного перед повторной попыткой',
  'Email rate limit exceeded': 'Слишком много попыток. Подождите немного',
  'rate_limit_exceeded': 'Слишком много попыток. Подождите немного',
  'over_request_rate_limit': 'Слишком много запросов. Подождите немного',
  'over_email_send_rate_limit': 'Письмо уже отправлено. Подождите минуту перед повторной попыткой',
  
  // Session errors
  'Session expired': 'Сессия истекла. Войдите снова',
  'session_expired': 'Сессия истекла. Войдите снова',
  'Invalid Refresh Token': 'Сессия истекла. Войдите снова',
  'Refresh Token Not Found': 'Сессия истекла. Войдите снова',
  'invalid_grant': 'Сессия истекла. Войдите снова',
  
  // User errors
  'User not found': 'Пользователь не найден',
  'user_not_found': 'Пользователь не найден',
  'User banned': 'Аккаунт заблокирован',
  'user_banned': 'Аккаунт заблокирован',
  
  // OAuth errors
  'Provider not enabled': 'Этот способ входа временно недоступен',
  'OAuth error': 'Ошибка входа через сторонний сервис',
  'oauth_error': 'Ошибка входа через сторонний сервис',
  'access_denied': 'Доступ запрещён',
  
  // Password reset
  'Password recovery email sent': 'Письмо для восстановления пароля отправлено',
  'For security purposes, you can only request this after 60 seconds': 'Подождите минуту перед повторным запросом',
  
  // Network/Server errors
  'Network request failed': 'Нет подключения к интернету',
  'fetch failed': 'Нет подключения к интернету',
  'Failed to fetch': 'Нет подключения к интернету',
  'NetworkError': 'Нет подключения к интернету',
  'Request timeout': 'Превышено время ожидания. Попробуйте снова',
  'timeout': 'Превышено время ожидания. Попробуйте снова',
  
  // Server errors
  'Internal server error': 'Ошибка сервера. Попробуйте позже',
  'internal_error': 'Ошибка сервера. Попробуйте позже',
  'unexpected_failure': 'Временная ошибка сервера. Попробуйте ещё раз',
  'Database error': 'Ошибка сервера. Попробуйте позже',
  'Database error saving new user': 'Ошибка при создании аккаунта. Попробуйте другой email или повторите позже',
  'duplicate key value violates unique constraint': 'Этот email уже используется. Попробуйте войти',
  'Service unavailable': 'Сервис временно недоступен',
  
  // OTP / Magic link
  'Token has expired or is invalid': 'Ссылка устарела. Запросите новую',
  'otp_expired': 'Код подтверждения устарел. Запросите новый',
  'otp_disabled': 'Этот способ входа недоступен',
  
  // Signup errors
  'Signups not allowed': 'Регистрация временно недоступна',
  'signups_disabled': 'Регистрация временно недоступна',
  
  // Validation
  'requested path is invalid': 'Неверная ссылка. Попробуйте войти заново',
  'bad_request': 'Некорректный запрос',
  'validation_failed': 'Проверьте правильность введённых данных',
  
  // MFA
  'MFA required': 'Требуется двухфакторная аутентификация',
  'mfa_required': 'Требуется двухфакторная аутентификация',
  'Invalid MFA code': 'Неверный код подтверждения',
  
  // Generic
  'Bad Request': 'Некорректный запрос',
  'Unauthorized': 'Необходима авторизация',
  'Forbidden': 'Доступ запрещён',
  'Not Found': 'Не найдено',
};

/**
 * Translate Supabase auth error to user-friendly Russian message
 */
export function translateAuthError(error: Error | string | null | undefined): string {
  if (!error) {
    return 'Произошла неизвестная ошибка';
  }
  
  const errorMessage = typeof error === 'string' ? error : error.message;
  
  // Try exact match first
  if (ERROR_TRANSLATIONS[errorMessage]) {
    return ERROR_TRANSLATIONS[errorMessage];
  }
  
  // Try partial match (for errors that contain these strings)
  for (const [key, translation] of Object.entries(ERROR_TRANSLATIONS)) {
    if (errorMessage.toLowerCase().includes(key.toLowerCase())) {
      return translation;
    }
  }
  
  // Check for common patterns
  if (errorMessage.includes('rate limit') || errorMessage.includes('too many') || errorMessage.includes('security purposes')) {
    return 'Подождите немного перед повторной попыткой';
  }
  
  if (errorMessage.includes('duplicate key') || errorMessage.includes('unique constraint')) {
    return 'Этот email уже используется. Попробуйте войти';
  }
  
  if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorMessage.includes('connection')) {
    return 'Нет подключения к интернету';
  }
  
  if (errorMessage.includes('server') || errorMessage.includes('500') || errorMessage.includes('503')) {
    return 'Ошибка сервера. Попробуйте позже';
  }
  
  if (errorMessage.includes('timeout')) {
    return 'Превышено время ожидания. Попробуйте снова';
  }
  
  if (errorMessage.includes('password')) {
    return 'Проверьте правильность пароля';
  }
  
  if (errorMessage.includes('email')) {
    return 'Проверьте правильность email';
  }
  
  if (errorMessage.includes('token') || errorMessage.includes('session')) {
    return 'Сессия истекла. Войдите снова';
  }
  
  // Default fallback
  return 'Произошла ошибка. Попробуйте снова';
}

/**
 * Check if error is a network/connectivity error
 */
export function isNetworkError(error: Error | string | null | undefined): boolean {
  if (!error) return false;
  const msg = typeof error === 'string' ? error : error.message;
  return msg.toLowerCase().includes('network') || 
         msg.toLowerCase().includes('fetch') || 
         msg.toLowerCase().includes('connection') ||
         msg.toLowerCase().includes('offline');
}

/**
 * Check if error is a rate limiting error
 */
export function isRateLimitError(error: Error | string | null | undefined): boolean {
  if (!error) return false;
  const msg = typeof error === 'string' ? error : error.message;
  return msg.toLowerCase().includes('rate limit') || 
         msg.toLowerCase().includes('too many') ||
         msg.toLowerCase().includes('security purposes') ||
         /\d+\s*seconds/i.test(msg);
}

/**
 * Extract seconds from rate limit error message
 */
export function extractSecondsFromRateLimitMessage(error: Error | string | null | undefined): number | null {
  if (!error) return null;
  const msg = typeof error === 'string' ? error : error.message;
  const match = msg.match(/after\s+(\d+)\s*seconds/i) || 
                msg.match(/every\s+(\d+)\s*seconds/i) ||
                msg.match(/(\d+)\s*seconds/i);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Check if error indicates user already exists
 */
export function isUserExistsError(error: Error | string | null | undefined): boolean {
  if (!error) return false;
  const msg = typeof error === 'string' ? error : error.message;
  return msg.toLowerCase().includes('already registered') || 
         msg.toLowerCase().includes('already exists');
}

/**
 * Check if error indicates invalid credentials
 */
export function isInvalidCredentialsError(error: Error | string | null | undefined): boolean {
  if (!error) return false;
  const msg = typeof error === 'string' ? error : error.message;
  return msg.toLowerCase().includes('invalid login credentials') || 
         msg.toLowerCase().includes('invalid credentials') ||
         msg.toLowerCase().includes('invalid_credentials');
}

/**
 * Check if error indicates email not confirmed
 */
export function isEmailNotConfirmedError(error: Error | string | null | undefined): boolean {
  if (!error) return false;
  const msg = typeof error === 'string' ? error : error.message;
  return msg.toLowerCase().includes('email not confirmed') || 
         msg.toLowerCase().includes('email_not_confirmed');
}
